import {
  cloneElement,
  JSX,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {styled} from 'styled-components';

import {keys} from '@shared/lib/map_utils';
import {neverHappensSilent} from '@shared/lib/type_utils';

import {Custom} from '@shared-web/lib/react';
import {cssPx} from '@shared-web/lib/styled_utils';

interface TooltipTheme {
  arrowWidth: number;
  arrowHeight: number;
  backgroundColor: string;
  arrowColor: string;
  textColor: string;
  fontSize: string | number;
  borderRadius: string;
  padding: string;
  borderColor: string;
}

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

interface TooltipProps extends Partial<TooltipTheme> {
  position?: TooltipPosition;
  // text?: string;
  content: ReactNode;
  maxWidth?: number;
  children: JSX.Element;
  trigger?: 'hover' | 'always' | 'never';
  tooltipRef?: MutableRefObject<HTMLDivElement | null>;
}

export const Tooltip: Custom<TooltipProps, 'div', 'content'> = ({
  position,
  content,
  maxWidth,
  fontSize,
  padding,
  backgroundColor,
  arrowColor,
  borderRadius,
  borderColor,
  trigger = 'hover',
  children,
  tooltipRef,
  ...rest
}) => {
  // const childrenRef = useRef<HTMLDivElement>(null);
  const randomId = useId();
  const childrenId = useMemo(
    () => (children.props.id as string | undefined) ?? randomId,
    [children.props.id, randomId]
  );

  const [hidden, setHidden] = useState(trigger !== 'always');

  const handleMouseEnter = useCallback(() => {
    if (trigger === 'hover') {
      setHidden(false);
    }
  }, [trigger]);
  const handleMouseLeave = useCallback(() => {
    if (trigger === 'hover') {
      setHidden(true);
    }
  }, [trigger]);

  useEffect(() => {
    if (trigger === 'always') {
      setHidden(false);
    } else if (trigger === 'never') {
      setHidden(true);
    }
  }, [trigger]);

  const themeInfo = useMemo(() => {
    return {
      arrowWidth: 8,
      arrowHeight: 4,
      backgroundColor: '#000000dd',
      arrowColor: '#000000dd',
      textColor: '#ffffff',
      fontSize: 12,
      borderRadius: 3,
      padding: '4px 8px',
      borderColor: 'transparent',
    };
  }, []);

  const [forceCompute, setForceCompute] = useState(Math.random());

  const handleResize = useCallback(() => {
    setForceCompute(Math.random());
  }, []);

  const observer = useRef<ResizeObserver>(new ResizeObserver(handleResize));
  const tooltipInternalRef = useRef<HTMLDivElement | null>(null);
  const tooltipContentRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (tooltipRef) {
        tooltipRef.current = node;
      }
      tooltipInternalRef.current = node;
      observer.current.disconnect();
      if (node) {
        observer.current.observe(node);
      }
    },
    [tooltipRef]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: `children` and `forceCompute` needs to be included in the deps since we want to recompute the positions if it changes.
  const computedPosition = useCallback(() => {
    const childrenElement = document.getElementById(childrenId);
    const tooltipContent = tooltipInternalRef.current;

    if (!(tooltipContent && childrenElement) || hidden) {
      return undefined;
    }
    const viewport = {width: document.body.clientWidth, height: document.body.clientHeight};
    const childrenRect = childrenElement.getBoundingClientRect();
    const tooltipRect = tooltipContent.getBoundingClientRect();

    // Compute how much space is available on all side of the children element
    const topSpace = childrenRect.y;
    const bottomSpace = viewport.height - childrenRect.y - childrenRect.height;
    const leftSpace = childrenRect.x;
    const rightSpace = viewport.width - childrenRect.x - childrenRect.width;

    // Compute the "fit ratio" for each position
    // The fit ratio is the ratio of the space available to the space needed by the tooltip content
    const fitScores: Record<TooltipPosition, {vRatio: number; hRatio: number}> = {
      top: {
        vRatio: topSpace / (tooltipRect.height + themeInfo.arrowHeight),
        hRatio: viewport.width / tooltipRect.width,
      },
      right: {
        vRatio: viewport.height / tooltipRect.height,
        hRatio: rightSpace / (tooltipRect.width + themeInfo.arrowWidth),
      },
      bottom: {
        vRatio: bottomSpace / (tooltipRect.height + themeInfo.arrowHeight),
        hRatio: viewport.width / tooltipRect.width,
      },
      left: {
        vRatio: viewport.height / tooltipRect.height,
        hRatio: leftSpace / (tooltipRect.width + themeInfo.arrowWidth),
      },
    };

    // Map between a preferred position and the list of all position ordered by most desirability
    const bestPositions: Record<TooltipPosition, TooltipPosition[]> = {
      top: ['top', 'bottom', 'right', 'left'],
      right: ['right', 'left', 'top', 'bottom'],
      bottom: ['bottom', 'top', 'right', 'left'],
      left: ['left', 'right', 'top', 'bottom'],
    };

    // Find the best position where the tooltip fits
    const bestPosition =
      position !== undefined
        ? // If a position is provided, use it to get the ordered list of preferred positions.
          // Then select the first position that fits.
          bestPositions[position].reduce<TooltipPosition | undefined>(
            (agg, val) =>
              agg ?? (fitScores[val].vRatio >= 1 && fitScores[val].hRatio >= 1 ? val : undefined),
            undefined
          ) ?? position
        : // If no position is provided, use the position that fits the best.
          (keys(bestPositions).sort((a, b) => {
            const worseScoreA = Math.min(fitScores[a].vRatio, fitScores[a].hRatio);
            const worseScoreB = Math.min(fitScores[b].vRatio, fitScores[b].hRatio);
            if (worseScoreA !== worseScoreB) {
              return worseScoreB - worseScoreA; // Prefer the best worse score
            }
            const betterScoreA = Math.max(fitScores[a].vRatio, fitScores[a].hRatio);
            const betterScoreB = Math.max(fitScores[b].vRatio, fitScores[b].hRatio);
            return betterScoreB - betterScoreA; // If worse scores are equal, prefer the best better score
          })[0] as TooltipPosition);

    // Compute the position of the svg where we draw the arrow
    const tooltipArrowRect = (() => {
      if (bestPosition === 'top') {
        return {
          width: tooltipRect.width,
          height: themeInfo.arrowHeight,
          bottom: -themeInfo.arrowHeight,
          left: 0,
        };
      }
      if (bestPosition === 'right') {
        return {
          width: themeInfo.arrowHeight,
          height: tooltipRect.height,
          top: 0,
          left: -themeInfo.arrowHeight,
        };
      }
      if (bestPosition === 'bottom') {
        return {
          width: tooltipRect.width,
          height: themeInfo.arrowHeight,
          top: -themeInfo.arrowHeight,
          left: 0,
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (bestPosition === 'left') {
        return {
          width: themeInfo.arrowHeight,
          height: tooltipRect.height,
          top: 0,
          right: -themeInfo.arrowHeight,
        };
      }
      neverHappensSilent(bestPosition);
      return {width: 0, height: 0, top: 0, left: 0};
    })();

    // Compute the position of the tooltip itself
    const tooltipPosition = (() => {
      if (bestPosition === 'top') {
        return {
          top: childrenRect.y - (tooltipRect.height + themeInfo.arrowHeight),
          left: childrenRect.x + (childrenRect.width - tooltipRect.width) / 2,
        };
      }
      if (bestPosition === 'right') {
        return {
          top: childrenRect.y + (childrenRect.height - tooltipRect.height) / 2,
          left: childrenRect.x + childrenRect.width + themeInfo.arrowHeight,
        };
      }
      if (bestPosition === 'bottom') {
        return {
          top: childrenRect.y + childrenRect.height + themeInfo.arrowHeight,
          left: childrenRect.x + (childrenRect.width - tooltipRect.width) / 2,
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (bestPosition === 'left') {
        return {
          top: childrenRect.y + (childrenRect.height - tooltipRect.height) / 2,
          left: childrenRect.x - (tooltipRect.width + themeInfo.arrowHeight),
        };
      }
      neverHappensSilent(bestPosition);
      return {top: 0, left: 0};
    })();

    // Compute the shift to apply to the tooltip to avoid it from being cut by the viewport
    let shiftLeft = 0;
    if (viewport.width < tooltipPosition.left + tooltipRect.width + themeInfo.arrowHeight) {
      shiftLeft = viewport.width - tooltipPosition.left - tooltipRect.width - themeInfo.arrowHeight;
      tooltipPosition.left += shiftLeft;
    }
    let shiftTop = 0;
    if (viewport.height < tooltipPosition.top + tooltipRect.height + themeInfo.arrowHeight) {
      shiftTop = viewport.height - tooltipPosition.top - tooltipRect.height - themeInfo.arrowHeight;
      tooltipPosition.top += shiftTop;
    }

    // Compute the transformation to place the arrow in the SVG.
    // The base arrow is drawn by default at the top left corner, facing down.
    const tooltipArrowTransforms: string[] = (() => {
      const {width, height} = tooltipArrowRect;
      if (bestPosition === 'top') {
        return [`translate(${(width - themeInfo.arrowWidth) / 2 - shiftLeft}, 0)`, 'rotate(0)'];
      }
      if (bestPosition === 'right') {
        return [
          `translate(${themeInfo.arrowHeight - shiftLeft}, ${
            (height - themeInfo.arrowWidth) / 2 + shiftTop
          })`,
          'rotate(90)',
        ];
      }
      if (bestPosition === 'bottom') {
        return [
          `translate(${(width + themeInfo.arrowWidth) / 2 - shiftLeft}, ${
            themeInfo.arrowHeight + shiftTop
          })`,
          'rotate(180)',
        ];
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (bestPosition === 'left') {
        return [`translate(0, ${(height + themeInfo.arrowWidth) / 2 + shiftTop})`, 'rotate(270)'];
      }
      neverHappensSilent(bestPosition);
      return [];
    })();

    // Rendering of the tooltip arrow SVG
    const tooltipArrow = (
      <TooltipArrow style={{...tooltipArrowRect}}>
        <polygon
          style={{transformBox: 'fill-box', transformOrigin: '0 0'}}
          points={`0 0 ${themeInfo.arrowWidth / 2} ${themeInfo.arrowHeight} ${
            themeInfo.arrowWidth
          } 0`}
          fill={themeInfo.arrowColor}
          stroke={themeInfo.borderColor}
          strokeWidth={1}
          transform={tooltipArrowTransforms.join(' ')}
        />
      </TooltipArrow>
    );
    return {tooltipPosition, tooltipArrow};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, position, childrenId, themeInfo, children, forceCompute]);

  const portal = hidden ? (
    <></>
  ) : (
    createPortal(
      <TooltipContent
        ref={tooltipContentRefCallback}
        style={{
          ...computedPosition()?.tooltipPosition,
        }}
        $backgroundColor={themeInfo.backgroundColor}
        $fontSize={themeInfo.fontSize}
        $color={themeInfo.textColor}
        $borderRadius={themeInfo.borderRadius}
        $padding={themeInfo.padding}
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        $opacity={hidden ? 0 : 1}
        $borderColor={themeInfo.borderColor}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div {...rest}>{content}</div>
        {computedPosition()?.tooltipArrow}
      </TooltipContent>,
      document.body
    )
  );

  return (
    <>
      {portal}
      {cloneElement(children, {
        id: childrenId,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      })}
    </>
  );
};
Tooltip.displayName = 'Tooltip';

const TooltipContent = styled.div<{
  $borderColor: string;
  $backgroundColor: string;
  $fontSize: string | number;
  $color: string;
  $borderRadius: string | number;
  $padding: string | number;
  $opacity: number;
}>`
  position: absolute;
  top: -1000px;
  left: -1000px;
  z-index: 100;
  border: solid 1px ${p => p.$borderColor};
  background-color: ${p => p.$backgroundColor};
  color: ${p => p.$color};
  border-radius: ${p => cssPx(p.$borderRadius)};
  padding: ${p => cssPx(p.$padding)};
  opacity: ${p => p.$opacity};
  font-size: ${p => cssPx(p.$fontSize)};
`;

const TooltipArrow = styled.svg`
  position: absolute;
`;
